import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Camera, Car, History, User } from "lucide-react";
import { RecordHeader, StatusBar, SmartButton, SmartButtons } from "@/components/layout/Record";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, SectionTitle, Field, LoadingState, ErrorState, StatusBadge, ConfirmDialog } from "@/components/common";
import { JobTracker, JOB_STEPS } from "@/modules/interventions/shared";
import { MechanicSelect } from "@/modules/interventions/NewJobDialog";
import { JobMaintenanceCard } from "@/modules/followups/shared";
import { MessageCustomerButton } from "@/modules/messaging/Inbox";
import { ChecklistCard, ObservationsCard, NotesCard, PhotosCard, QuotesCard, LogCard, run } from "@/modules/interventions/JobSections";
import { useGarage } from "@/context/GarageContext";
import { DiagnosticsCard } from "@/modules/diagnostics/Diagnostics";
import { TimeCard } from "@/modules/interventions/TimeCard";
import { useFetch } from "@/lib/useFetch";
import { api, downloadFile } from "@/lib/api";
import { FileDown } from "lucide-react";
import { fmtNumber } from "@/lib/format";
import { t } from "@/i18n";

function ReportActions({ job }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" onClick={() => downloadFile(`/jobs/${job.id}/report.pdf`)} data-testid="job-report-download"><FileDown className="mr-1.5 h-4 w-4" />{t("b14.legacyPdf")}</Button>
      
    </div>
  );
}

function JobStatusBar({ job, setJob, readOnly }) {
  const [cancel, setCancel] = useState(false);
  const go = (status) => run(() => api.post(`/jobs/${job.id}/status`, { status }), setJob, t("jobs.statusChanged", { status: t(`status.${status}`) }));
  const allowed = readOnly ? [] : job.next_statuses.filter((s) => s !== "cancelled");
  const next = allowed.find((s) => JOB_STEPS.indexOf(s) > JOB_STEPS.indexOf(job.status));
  return (
    <div className="space-y-3" data-testid="job-status-actions">
      {job.status === "cancelled" ? <JobTracker status="cancelled" /> : <StatusBar steps={JOB_STEPS} current={job.status} allowed={allowed} onPick={go} idPrefix="job-to" />}
      {!readOnly && job.next_statuses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {next && <Button onClick={() => go(next)} data-testid={`job-next-${next}`}>{t(`jobs.to.${next}`)}</Button>}
          {job.next_statuses.includes("cancelled") && <Button variant="ghost" className="text-red-600" onClick={() => setCancel(true)} data-testid="job-to-cancelled">{t("jobs.to.cancelled")}</Button>}
        </div>
      )}
      <ConfirmDialog open={cancel} onOpenChange={setCancel} destructive title={t("jobs.cancelTitle")} description={t("jobs.cancelText")} confirmLabel={t("jobs.to.cancelled")}
        onConfirm={() => { setCancel(false); go("cancelled"); }} testId="job-cancel-dialog" />
    </div>
  );
}

function InfoCard({ job, setJob, readOnly }) {
  const { isManager } = useGarage();
  const [f, setF] = useState({ reason: job.reason, promised_at: job.promised_at || "", odometer_in: job.odometer_in ?? "", mechanic_user_id: job.mechanic?.id || null });
  const locked = readOnly || ["completed", "cancelled"].includes(job.status);
  const save = () => run(() => api.put(`/jobs/${job.id}`, { reason: f.reason, promised_at: f.promised_at || null, odometer_in: f.odometer_in === "" ? null : Number(f.odometer_in),
    ...(isManager ? { mechanic_user_id: f.mechanic_user_id } : {}) }), setJob);
  return (
    <Card testId="job-info-card">
      <SectionTitle>{t("jobs.details")}</SectionTitle>
      <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
        <div><dt className="text-slate-500">{t("sched.customer")}</dt><dd className="font-medium" data-testid="job-customer">{job.customer.name} {job.customer.phone && `· ${job.customer.phone}`}</dd></div>
        <div><dt className="text-slate-500">{t("sched.vehicle")}</dt><dd className="font-medium" data-testid="job-vehicle"><span className="font-mono-plate">{job.vehicle.plate}</span> · {job.vehicle.make} {job.vehicle.model} {job.vehicle.year || ""}</dd></div>
        <div><dt className="text-slate-500">{t("jobs.lastOdometer")}</dt><dd className="font-medium" data-testid="job-vehicle-odometer">{fmtNumber(job.vehicle.odometer)} km {job.vehicle.odometer_date && `(${job.vehicle.odometer_date})`}</dd></div>
        {job.appointment_id && <div><dt className="text-slate-500">{t("jobs.fromBooking")}</dt><dd><Link to="/pro/planning" className="font-medium text-teal-700 underline">{t("sched.planning")}</Link></dd></div>}
      </dl>
      {job.customer.id && <div className="mb-4"><MessageCustomerButton vehicleId={job.vehicle.id} /></div>}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t("jobs.reason")} className="sm:col-span-3"><Textarea disabled={locked} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="job-edit-reason" /></Field>
        <Field label={t("jobs.odometer")}><Input disabled={locked} type="number" min={0} value={f.odometer_in} onChange={(e) => setF({ ...f, odometer_in: e.target.value })} data-testid="job-edit-odometer" /></Field>
        <Field label={t("jobs.promised")}><Input disabled={locked} type="date" value={f.promised_at} onChange={(e) => setF({ ...f, promised_at: e.target.value })} data-testid="job-edit-promised" /></Field>
        <Field label={t("sched.mechanic")}>{isManager && !locked ? <MechanicSelect value={f.mechanic_user_id} onChange={(v) => setF({ ...f, mechanic_user_id: v })} /> : <p className="pt-2 text-sm font-medium">{job.mechanic?.name || t("jobs.unassigned")}</p>}</Field>
      </div>
      {!locked && <Button className="mt-4" onClick={save} data-testid="job-info-save">{t("common.save")}</Button>}
    </Card>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { garage, readOnly } = useGarage();
  const { data, error, loading, reload } = useFetch(`/jobs/${id}`);
  const [job, setJob] = useState(null);
  useEffect(() => { if (data) setJob(data); }, [data]);
  const loaded = !!job;
  useEffect(() => { if (loaded && ["#checklist", "#inspection"].includes(window.location.hash)) document.getElementById("inspection")?.scrollIntoView({ behavior: "smooth" }); }, [loaded]);
  if (loading && !job) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!job) return null;
  const p = { job, setJob, readOnly };
  const legacy = !!job.report_shared_at || (job.checklist || []).some((i) => i.state || i.note);
  return (
    <div className="space-y-6" data-testid="job-detail">
      <RecordHeader crumbs={[[t("b13.nav.workshop"), "/pro/board"], [t("jobs.title"), "/pro/jobs"], [job.number]]}
        eyebrow={<span data-testid="job-number">{job.number}</span>} title={`${job.vehicle.plate} · ${job.vehicle.make} ${job.vehicle.model}`}
        badge={<StatusBadge status={job.status} testId="job-status" />} actions={legacy && <ReportActions job={job} />}
        statusBar={<JobStatusBar {...p} />}
        smart={<SmartButtons>
          <SmartButton icon={User} label={t("sched.customer")} value={job.customer.name} to={`/pro/customers/${job.customer.id}`} testId="smart-customer" />
          <SmartButton icon={Car} label={t("sched.vehicle")} value={job.vehicle.plate} to={`/pro/vehicles/${job.vehicle.id}`} testId="smart-vehicle" />
          <SmartButton icon={Camera} label={t("b13.smart.photos")} value={(job.photos || []).length} testId="smart-photos" />
          <SmartButton icon={History} label={t("b13.smart.vehicleJobs")} value={t("b13.smart.open")} to={`/pro/jobs?status=all&vehicle_id=${job.vehicle.id}`} testId="smart-vehicle-jobs" />
        </SmartButtons>} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.4fr_1fr] [&>*]:min-w-0">
        <div className="space-y-6">
          <InfoCard key={job.updated_at} {...p} />
          <div id="inspection" className="scroll-mt-20"><DiagnosticsCard job={job} readOnly={readOnly} /></div>
          {legacy && <ChecklistCard key={`c-${job.id}`} {...p} readOnly legacy />}
          <PhotosCard {...p} />
        </div>
        <div className="space-y-6">
          <JobMaintenanceCard job={job} />
          <TimeCard key={`t-${job.status}`} job={job} readOnly={readOnly} />
          <QuotesCard {...p} />
          <ObservationsCard {...p} />
          <NotesCard {...p} />
          <LogCard log={job.log} tz={garage.timezone} />
        </div>
      </div>
    </div>
  );
}
